import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const ShiftContext = createContext(null);

function fmtDuration(minutes) {
  if (!minutes || minutes < 0) return "0m";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function ShiftProvider({ children }) {
  const { user } = useAuth();
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(false);
  // tick to force re-render every minute for live timer
  const [, setNow] = useState(Date.now());

  const refresh = useCallback(async () => {
    if (!user || user === false) return;
    try {
      const { data } = await api.get("/shift/current");
      setShift(data);
    } catch {
      /* ignore */
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const action = useCallback(
    async (path) => {
      setLoading(true);
      try {
        const { data } = await api.post(path);
        setShift(data);
        return data;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const punchIn = () => action("/shift/punch-in");
  const punchOut = () => action("/shift/punch-out");
  const startBreak = () => action("/shift/break/start");
  const endBreak = () => action("/shift/break/end");

  const value = {
    shift,
    loading,
    refresh,
    punchIn,
    punchOut,
    startBreak,
    endBreak,
    fmtDuration,
  };

  return <ShiftContext.Provider value={value}>{children}</ShiftContext.Provider>;
}

export function useShift() {
  const ctx = useContext(ShiftContext);
  if (!ctx) throw new Error("useShift must be used inside ShiftProvider");
  return ctx;
}
